import { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button } from '@upmc/ims.component-library';

import classnames from 'classnames';
import { UserContext } from 'context/User/UserContext';
import { raiseErrorBanner } from '../../../../../components/MessageDispatch/messageDispatch';
import { getMyRequests } from './_api';
import { Widget } from '../../Widget/Widget';
import WithLoadingHandler from '../../../../../hoc/withLoadingHandler/withLoadingHandler';
import StyleAuxiliary from '../../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { DataTable } from '../../../../../components/UI/MDC/DataTable/DataTable';
import Card from '../../../../../components/UI/MDC/Card/Card';
import {
	Subtitle1,
	Subtitle2,
} from '../../../../../components/UI/MDC/Typography/Typography';
import { getLegacyPath } from 'routes/_utils';
import { RequestTypes } from '../../../../../constants/enums';
import { CSS_CLASSES, MAX_CHAR_CELL, MAX_ROWS } from './_constants';
import classes from './RequestWidget.module.scss';

const RequestWidget = ({ title, subtitle, type = '' }) => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.impersonationProfile ?? contextUser.userInfo.userProfile;
	const dispatch = useDispatch();
	const history = useHistory();

	const [sHeaders, setHeaders] = useState(null);
	const [sIsRefreshing, setIsRefreshing] = useState(false);
	const [sRows, setRows] = useState(null);
	const [sCount, setCount] = useState(null);
	const [sRedirectLocation, setRedirectLocation] = useState('');

	const render = async () => {
		let payload;
		let arrRows = [];
		let locationRedirect = sRedirectLocation;

		try {
			switch (type) {
				case RequestTypes.SELF:
					setHeaders([
						{ Value: 'Requestee' },
						{ Value: 'Applications' },
					]);

					payload = await getMyRequests(
						user.ID,
						null,
						null,
						true,
						MAX_ROWS,
						dispatch
					);
					locationRedirect = '/RRS/RequestsSubmittedForMe.aspx';
					break;
				case RequestTypes.SINGLE:
					setHeaders([
						{ Value: 'Requestee' },
						{ Value: 'Applications' },
					]);

					payload = await getMyRequests(
						user.ID,
						null,
						null,
						false,
						MAX_ROWS,
						dispatch
					);
					locationRedirect = '/RRS/Requests.aspx';
					break;
				default:
					break;
			}
		} catch (error) {
			raiseErrorBanner(error.cause ? error.cause : error, dispatch);
		}

		arrRows = constructRows(payload?.Requests);
		setCount(payload?.Count);
		setRows(arrRows);
		setTableHeight(true);
		setRedirectLocation(getLegacyPath(locationRedirect?.trim()));
		setIsRefreshing(false);
	};

	useEffect(() => {
		if (user?.ID) {
			render();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	/**
	 * Handler on widget refresh. Preserves request table height, triggers refresh animation, resets request data for re-retrieval.
	 * @returns void
	 */
	const onRefreshHandler = () => {
		setTableHeight(false);
		setIsRefreshing(true);
		setCount(0);
		setRows([]);
		render();
	};

	const constructRows = arrRequests => {
		let arrResult = arrRequests.map(request => {
			const KEY_NAME = Object.keys(request).find(key =>
				key.match(/request.*name/gi)
			);
			let name = request?.[KEY_NAME].trim();
			name = getFriendlyStringPart(name);

			// concatenate application names
			let applications = request?.Application?.reduce(
				(prevValue, currentValue, index) =>
					prevValue + (index === 0 ? '' : ', ') + currentValue?.Name,
				''
			);
			// increase character length relative to length of preceding column
			applications = getFriendlyStringPart(
				applications,
				MAX_CHAR_CELL + (request[KEY_NAME].length - name.length)
			);

			return {
				Key: request?.ID.toString(),
				Cells: {
					[KEY_NAME.substring(0, KEY_NAME.length - 4)]: {
						Value: name,
						Validation: {
							Key: 'true',
							Value: {
								ErrorMessage: null,
							},
						},
					},
					Applications: {
						Value: applications,
						Validation: {
							Key: 'true',
							Value: {
								ErrorMessage: null,
							},
						},
					},
				},
			};
		});
		return arrResult;
	};

	/**
	 * Conditionally splices `input` to a user-friendly length.
	 * @param {string} input string of interest.
	 * @param {number} [lengthFriendly=MAX_CHAR_CELL] optional desired length.
	 * @returns string with user-friendly length.
	 */
	const getFriendlyStringPart = (input, lengthFriendly = MAX_CHAR_CELL) =>
		input?.length > lengthFriendly
			? input.substring(0, lengthFriendly) + '...'
			: input;

	/**
	 * Sets custom styling on table container to preserve height of container across refreshes.
	 * @param {boolean} shouldReset whether to reset custom styling.
	 */
	const setTableHeight = shouldReset => {
		let elTable = document.getElementById(CSS_CLASSES.DATA_TABLE);
		if (elTable) {
			elTable.style.height = !shouldReset
				? `${elTable.offsetHeight}px`
				: '';
		}
	};

	return type ? (
		<Widget
			className={classes[CSS_CLASSES.WIDGET]}
			id={CSS_CLASSES.WIDGET}
			title={title}
			subtitle={subtitle}
			onRefresh={onRefreshHandler}
		>
			<WithLoadingHandler isLoading={sRows === null && sHeaders === null}>
				{sCount !== null && sRows !== null ? (
					sCount > 0 || sIsRefreshing ? (
						<div
							className={classnames({
								[`${classes[CSS_CLASSES.WIDGET_REFRESHING]}`]:
									sIsRefreshing,
							})}
							id={CSS_CLASSES.DATA_TABLE}
						>
							<DataTable
								headers={sHeaders}
								loading={
									Array.isArray(sRows) &&
									sRows.length === 0 &&
									sHeaders !== null &&
									sIsRefreshing
								}
								rows={sRows}
								onRowClickedHandler={() => {
									history.push(sRedirectLocation);
								}}
							/>
							{sCount > 0 && (
								<StyleAuxiliary classes='mdc-form-field'>
									<Subtitle2>
										Showing <strong>{sRows.length}</strong>{' '}
										of <strong>{sCount}</strong>.
									</Subtitle2>
									<Button
										sx={{ m: '5px' }}
										onClick={() => {
											history.push(sRedirectLocation);
										}}
										id={`${title}_ViewAll`}
										variant='outlined'
									>
										View All
									</Button>
								</StyleAuxiliary>
							)}
						</div>
					) : (
						<Card
							xlarge
							className={classes[CSS_CLASSES.WIDGET_EMPTY]}
						>
							<Subtitle1>
								You have submitted no recent requests.
							</Subtitle1>
						</Card>
					)
				) : null}
			</WithLoadingHandler>
		</Widget>
	) : null;
};

export { RequestWidget };
